import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Store, Package, AlertTriangle, RefreshCw, Download, Sparkles, Flag } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { useSuperAdminAuth } from '../../contexts/SuperAdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// What each error scope is called on screen, so a failure names the thing that failed.
// Environment values arrive lower-cased from the server ("staging"); present them readably
// without inventing a value when the server has none.
const titleCase = (v) => (v ? String(v).charAt(0).toUpperCase() + String(v).slice(1) : '');

// Which review actions this listing accepts comes from the SERVER, on each row as
// `allowed_decisions` (services/marketplace_moderation). The console renders that list rather than
// deciding for itself, so the buttons cannot drift from the rule that actually admits a decision —
// and a decision the server would refuse with 409 is never offered in the first place.
const ACTION_LABELS = {
  // Never a bare "Confirm" — the operator must see that this removes the listing from Customer
  // Marketplace before they click it.
  dismiss: 'Dismiss',
  confirm: 'Confirm Violation',
  request_correction: 'Request Correction',
  // Named for the governance outcome, not the mechanism. "Dismiss" would be wrong here twice over:
  // it is a different backend decision, and it describes throwing a finding away rather than
  // accepting work the tenant actually did.
  accept_correction: 'Accept Correction',
};

const ACTION_CONSEQUENCE = {
  dismiss: 'Marks the listing approved. It becomes eligible for Customer Marketplace again, subject to the participation and publication state of its showroom.',
  confirm: 'This will remove the listing from Customer Marketplace visibility. The listing is NOT deleted and its content is not edited.',
  request_correction: 'Records a correction request. The moderation status is unchanged and the tenant keeps ownership of its own content — Servisfy does not edit the listing.',
  accept_correction: 'Closes the correction. The tenant has resubmitted and you are accepting their fix. Nothing about the listing changes — it was approved and Customer-visible throughout — and the original request, the tenant resubmission and this decision all stay in Governance History.',
};

// Reason is mandatory for the two actions that take something away or place an obligation on the
// tenant. Mirrors the backend contract, which 422s without one.
const ACTION_REQUIRES_REASON = {
  dismiss: false, confirm: true, request_correction: true,
  // Accepting takes nothing away and places no obligation, so no reason is demanded — the same rule
  // the backend applies (REASON_REQUIRED_FOR). An optional note is still recorded if one is given.
  accept_correction: false,
};

const SCOPE_LABELS = {
  summary: 'Summary metrics',
  showrooms: 'Showroom registry',
  categories: 'Category catalogue',
  moderation: 'Moderation review',
  tenant: 'Tenant detail',
  analysis: 'Analysis',
  flagged: 'Flagged listings',
  participation: 'Participation change',
  visibility: 'Listing visibility',
  review: 'Review decision',
};

// Friendly labels over the CANONICAL participation states. The label is presentation; the value
// stays exactly what the backend stores, so nothing here can invent or rename a governance state.
const PARTICIPATION_LABELS = {
  legacy_active: 'Legacy Active',
  pending_review: 'Pending Review',
  approved: 'Approved',
  correction_required: 'Correction Required',
  suspended: 'Suspended',
  inactive: 'Inactive',
};

// Transitions a governor may START from the console. legacy_active is deliberately ABSENT: it is a
// migration state and the backend refuses to assign it, so offering it would be a dead control.
const PARTICIPATION_ACTIONS = [
  { state: 'approved', label: 'Approve' },
  { state: 'correction_required', label: 'Request Correction' },
  { state: 'suspended', label: 'Suspend' },
  { state: 'inactive', label: 'Deactivate' },
];

// EMT SuperAdmin (2026-06-17): platform marketplace governance. Every button is
// backed by a real endpoint — counts (/marketplace/summary), showrooms
// (/marketplace/showrooms), analysis (/marketplace/analysis), flagged content
// (/marketplace/flagged), export (real CSV). No fake metrics, no tenant payloads.

const MarketplaceControl = () => {
  const { superAdmin } = useSuperAdminAuth();
  const [summary, setSummary] = useState(null);
  const [showrooms, setShowrooms] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [flagged, setFlagged] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  // Errors are scoped PER SECTION. A single shared `error` meant a Categories 500 painted a
  // page-level "HTTP 500" banner indistinguishable from a summary failure, so the operator could
  // not tell what had actually failed or what was safe to trust on screen. Each entry is
  // { message, retry } so the banner can offer the specific action that failed.
  const [errors, setErrors] = useState({});
  const failScope = (scope, err, retry) => setErrors((prev) => ({
    ...prev,
    [scope]: {
      message: err?.response?.data?.detail
        || (err?.response?.status ? `HTTP ${err.response.status}` : 'network error'),
      retry,
    },
  }));
  const clearScope = (scope) => setErrors((prev) => {
    if (!(scope in prev)) return prev;
    const next = { ...prev }; delete next[scope]; return next;
  });
  // Sections inside the ONE Marketplace Control surface. Deliberately not a new top-level page:
  // the governance authorities all extend this console.
  const [section, setSection] = useState('overview');
  const [categories, setCategories] = useState([]);
  const [moderation, setModeration] = useState(null);
  const [tenant, setTenant] = useState(null);          // drill-down payload
  const [tenantTab, setTenantTab] = useState('overview');
  // The one governed review modal: { listing, action }.
  const [review, setReview] = useState(null);
  const [reviewReason, setReviewReason] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  const apiRef = useRef(null);
  if (!apiRef.current) {
    const instance = axios.create({ baseURL: BACKEND_URL });
    instance.interceptors.request.use((config) => {
      const token = superAdmin?.token || localStorage.getItem('superadmin_session') ||
        localStorage.getItem('superadmin_session_token') || localStorage.getItem('superAdminToken') ||
        localStorage.getItem('superadminToken');
      if (token) { config.headers = config.headers || {}; config.headers.Authorization = `Bearer ${token}`; }
      return config;
    });
    apiRef.current = instance;
  }
  const api = apiRef.current;

  const fetchAll = async () => {
    setLoading(true);
    clearScope('summary'); clearScope('showrooms');
    // Two independent reads: one failing must not blank the other, and each reports its own scope.
    const [s, sr] = await Promise.allSettled([
      api.get('/api/v1/super-admin/marketplace/summary'),
      api.get('/api/v1/super-admin/marketplace/showrooms'),
    ]);
    if (s.status === 'fulfilled') setSummary(s.value.data);
    else failScope('summary', s.reason, fetchAll);
    if (sr.status === 'fulfilled') setShowrooms(Array.isArray(sr.value.data) ? sr.value.data : []);
    else failScope('showrooms', sr.reason, fetchAll);
    setLoading(false);
  };

  // Refresh must re-read CURRENT canonical state, not just the registry. Section payloads are
  // invalidated so the open tab refetches instead of showing a value from before the refresh.
  const refreshAll = async () => {
    setCategories([]); setModeration(null); setAnalysis(null); setFlagged(null);
    setErrors({});
    await fetchAll();
    if (section === 'categories') await fetchCategories();
    if (section === 'moderation') await fetchModeration();
    if (tenant?.business_id) await openTenant(tenant.business_id);
  };
  useEffect(() => { fetchAll(); }, []);

  const fetchCategories = async () => {
    setBusy('categories'); clearScope('categories');
    try { const r = await api.get('/api/v1/super-admin/marketplace/categories'); setCategories(r.data?.categories || []); }
    catch (err) { failScope('categories', err, fetchCategories); }
    finally { setBusy(null); }
  };

  const fetchModeration = async () => {
    setBusy('moderation');
    // Canonical signal: db.products.moderation_status / is_approved. NOT db.flagged_content, which
    // has no writer anywhere and was therefore permanently empty.
    clearScope('moderation');
    try { const r = await api.get('/api/v1/super-admin/marketplace/moderation'); setModeration(r.data); }
    catch (err) { failScope('moderation', err, fetchModeration); }
    finally { setBusy(null); }
  };

  const openTenant = async (businessId) => {
    if (!businessId) return;
    setBusy('tenant'); setTenantTab('overview'); clearScope('tenant');
    try { const r = await api.get(`/api/v1/super-admin/marketplace/tenants/${encodeURIComponent(businessId)}`); setTenant(r.data); }
    catch (err) { failScope('tenant', err, () => openTenant(businessId)); }
    finally { setBusy(null); }
  };

  const transitionParticipation = async (businessId, state) => {
    // legacy_active is migration-only and the backend refuses it; it is never offered here.
    const needsReason = ['suspended', 'correction_required', 'inactive'].includes(state);
    const reason = needsReason
      ? (typeof window !== 'undefined' ? window.prompt(`Reason for "${state}" (required)`) : '')
      : null;
    if (needsReason && !(reason || '').trim()) return;
    setBusy('participation');
    try {
      await api.post(`/api/v1/super-admin/marketplace/participation/${encodeURIComponent(businessId)}`,
        { state, reason });
      await fetchAll();
      await openTenant(businessId);
    } catch (err) {
      failScope('participation', err, null);
    } finally { setBusy(null); }
  };

  // Tenant detail belongs to the Showrooms tab. Selecting another section drops it so no stale
  // panel survives into a tab that does not own it.
  const goToSection = (id) => {
    if (id !== 'overview') setTenant(null);
    setSection(id);
  };
  const openModeration = () => { setTenant(null); setSection('moderation'); fetchModeration(); };

  const openReview = (listing, action) => {
    setReview({ listing, action });
    setReviewReason('');
    clearScope('review');
  };

  // Submit a governed review decision. The listing's own copy is never touched here — the backend
  // writes only moderation_status / is_approved / is_active / moderation_review.
  const submitReview = async () => {
    if (!review) return;
    const { listing, action } = review;
    const reason = reviewReason.trim();
    if (ACTION_REQUIRES_REASON[action] && !reason) return;   // backend 422s; do not send it
    setReviewSubmitting(true);
    clearScope('review');
    try {
      await api.post(
        `/api/v1/super-admin/marketplace/moderation/${encodeURIComponent(listing.id)}/review`,
        { decision: action, reason: reason || null },
      );
      setReview(null); setReviewReason('');
      // Re-read everything the decision can move: the queue, the registry counts, and — if a tenant
      // is open — its detail, so Governance History shows the event that was just recorded.
      await fetchModeration();
      await fetchAll();
      if (tenant?.overview?.business_id) await openTenant(tenant.overview.business_id);
    } catch (err) {
      failScope('review', err, null);
    } finally { setReviewSubmitting(false); }
  };

  // Visibility is a SEPARATE governance action from a moderation verdict: it moves only
  // `is_active`, leaving moderation_status and is_approved untouched. It is also the ONLY way to
  // bring a listing back after a confirmed violation — `dismiss` restores the moderation verdict
  // but deliberately does not re-publish, so reinstatement stays an explicit decision.
  const setVisibility = async (listing, active) => {
    const reason = active
      ? null
      : (typeof window !== 'undefined' ? window.prompt('Reason for suspending this listing (required)') : '');
    if (!active && !(reason || '').trim()) return;
    setBusy('visibility'); clearScope('visibility');
    try {
      await api.post(
        `/api/v1/super-admin/marketplace/listings/${encodeURIComponent(listing.id)}/visibility`,
        { active, reason: reason || null },
      );
      await fetchModeration();
      await fetchAll();
      if (tenant?.overview?.business_id) await openTenant(tenant.overview.business_id);
    } catch (err) {
      failScope('visibility', err, null);
    } finally { setBusy(null); }
  };

  useEffect(() => {
    if (section === 'categories' && categories.length === 0) fetchCategories();
    if (section === 'moderation' && moderation === null) fetchModeration();
  }, [section]);

  const runAnalysis = async () => {
    setBusy('analysis'); clearScope('analysis');
    try { const r = await api.get('/api/v1/super-admin/marketplace/analysis'); setAnalysis(r.data); setFlagged(null); }
    catch (err) { failScope('analysis', err, runAnalysis); }
    finally { setBusy(null); }
  };
  const reviewFlagged = async () => {
    setBusy('flagged'); clearScope('flagged');
    try { const r = await api.get('/api/v1/super-admin/marketplace/flagged'); setFlagged(r.data?.flagged || []); setAnalysis(null); }
    catch (err) { failScope('flagged', err, reviewFlagged); }
    finally { setBusy(null); }
  };

  const exportShowrooms = () => {
    // Export mirrors the table: canonical values only. An unknown value exports EMPTY, never a
    // stand-in string, so a governance export can never be mistaken for recorded fact.
    const rows = [['business_id', 'name', 'contact', 'marketplace_state', 'listing_visibility',
                   'environment', 'is_test', 'publicly_discoverable', 'listings', 'active_listings',
                   'compliance_findings', 'showroom_created']];
    showrooms.forEach((s) => rows.push([
      s.business_id || '', s.name || '', s.contact || '',
      s.participation_state || '', s.listing_visibility || '',
      s.environment || '',
      s.is_test ? 'true' : 'false',
      typeof s.publicly_discoverable === 'boolean' ? String(s.publicly_discoverable) : '',
      typeof s.listings === 'number' ? s.listings : '',
      typeof s.active_listings === 'number' ? s.active_listings : '',
      typeof s.findings === 'number' ? s.findings : '',
      s.joinedAt || '',
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'showrooms.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (v) => (v === null || v === undefined ? '—' : String(v));
  const Metric = ({ label, value }) => (
    <Card className="bg-white shadow-md"><CardContent className="p-6">
      <p className="text-sm text-gray-600 mb-1">{label}</p>
      <p className="text-3xl font-bold text-gray-900">{loading ? '…' : value}</p>
    </CardContent></Card>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Marketplace Control</h1>
          <p className="text-gray-600 mt-1">Platform marketplace governance (live)</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={refreshAll} variant="outline" disabled={loading}><RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh</Button>
          <Button onClick={runAnalysis} variant="outline" disabled={!!busy}><Sparkles className="h-4 w-4 mr-2" />{busy === 'analysis' ? 'Analyzing…' : 'Run Analysis'}</Button>
          {/* Renamed: this panel reads db.products.moderation_status, the canonical signal. The old
              label promised a "flagged content" queue backed by db.flagged_content, which has no
              writer anywhere in the repository and was therefore permanently empty. */}
          {/* Opens the moderation workspace. It also drops any open tenant detail: that panel
              belongs to the Showrooms tab, and leaving it mounted made Moderation look like it had
              opened "inside" whichever tenant happened to be selected. */}
          <Button onClick={openModeration} variant="outline" disabled={!!busy}><Flag className="h-4 w-4 mr-2" />Moderation Review</Button>
          <Button onClick={exportShowrooms} variant="outline" disabled={loading || showrooms.length === 0}><Download className="h-4 w-4 mr-2" />Export Showrooms</Button>
        </div>
      </div>

      {/* One banner PER failed scope, naming what failed and retrying only that. A single shared
          "HTTP 500" told the operator nothing about which part of the console to distrust. */}
      {Object.entries(errors).map(([scope, e]) => (
        <div key={scope} className="flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-200">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <p className="text-sm text-red-700">
            <span className="font-semibold">{SCOPE_LABELS[scope] || scope}</span> failed: {e.message}
          </p>
          {e.retry && (
            <Button onClick={e.retry} size="sm" variant="outline" className="ml-auto">Retry</Button>
          )}
        </div>
      ))}

      <div className="grid grid-cols-1 md:grid-cols-5 gap-5">
        <Metric label="Total Showrooms" value={fmt(summary?.total_showrooms)} />
        <Metric label="Active Showrooms" value={fmt(summary?.active_showrooms)} />
        {/* Real count from the SAME predicate Customer discovery uses. Added because "active" and
            "discoverable" are different facts and the console showed only the first. */}
        <Metric label="Discoverable" value={fmt(summary?.discoverable_showrooms)} />
        <Metric label="Total Products" value={fmt(summary?.total_products)} />
        <Metric label="Active Products" value={fmt(summary?.active_products)} />
      </div>

      {/* Sections of the ONE Marketplace Control console. */}
      <div className="flex gap-1 border-b border-gray-200">
        {[['overview', 'Showrooms'], ['categories', 'Categories'], ['moderation', 'Moderation']].map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => goToSection(id)}
            className={'px-4 py-2 text-sm font-medium border-b-2 -mb-px ' + (section === id
              ? 'border-purple-600 text-purple-700'
              : 'border-transparent text-gray-500 hover:text-gray-700')}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Showrooms-only. Keeping the payload in state is fine; RENDERING it in another tab is not. */}
      {section === 'overview' && tenant && (
        <Card className="bg-white shadow-md border-purple-200">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="text-lg">{tenant.overview?.name || 'Business'}</CardTitle>
                <p className="text-xs text-gray-500">Marketplace tenant detail</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setTenant(null)}>Close</Button>
            </div>
            <div className="flex gap-1 border-b border-gray-200 mt-3">
              {[['overview', 'Overview'], ['listings', 'Products & Services'],
                ['compliance', 'Category Compliance'], ['audit', 'Governance History']].map((e) => (
                <button
                  key={e[0]}
                  type="button"
                  onClick={() => setTenantTab(e[0])}
                  className={'px-3 py-2 text-sm border-b-2 -mb-px ' + (tenantTab === e[0]
                    ? 'border-purple-600 text-purple-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700')}
                >
                  {e[1]}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {tenantTab === 'overview' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  {[
                    ['Marketplace State', PARTICIPATION_LABELS[tenant.overview?.participation_state] || tenant.overview?.participation_state || 'not set'],
                    ['Showroom Visibility', tenant.overview?.listing_visibility || '—'],
                    ['Environment', tenant.overview?.environment
                      ? titleCase(tenant.overview.environment) : 'unavailable'],
                    ['Test data', tenant.overview?.is_test ? 'Yes' : 'No'],
                    ['Customer Discoverable', tenant.overview?.publicly_discoverable ? 'Yes' : 'Withheld'],
                    ['Listings', (tenant.overview?.active_listings ?? 0) + ' active / ' + (tenant.overview?.listings ?? 0)],
                    ['Services', tenant.overview?.services ?? 0],
                    ['Products', tenant.overview?.products ?? 0],
                    ['Unclassified', tenant.overview?.unclassified ?? 0],
                    ['Compliance findings',
                      typeof tenant.overview?.compliance_findings === 'number'
                        ? (tenant.overview.compliance_findings > 0
                            ? tenant.overview.compliance_findings
                            : 'Compliant — 0 findings')
                        : 'unavailable'],
                    ['Marketplace entitlement',
                      tenant.commercial?.marketplace_entitled === true ? 'Entitled'
                        : tenant.commercial?.marketplace_entitled === false ? 'Not entitled'
                        : 'Not available'],
                  ].map((e) => (
                    <div key={e[0]} className="p-3 rounded bg-gray-50">
                      <p className="text-xs text-gray-500">{e[0]}</p>
                      <p className="text-gray-900 font-medium">{String(e[1])}</p>
                    </div>
                  ))}
                </div>

                {/* Participation governance. legacy_active is deliberately NOT offered: it is a
                    migration state the backend refuses to assign, so a control for it would be
                    dead. Suspend / Request Correction / Deactivate prompt for a reason because the
                    state machine requires one. */}
                <div>
                  <p className="text-xs text-gray-500 mb-2">Participation governance (audited)</p>
                  <div className="flex gap-2 flex-wrap">
                    {PARTICIPATION_ACTIONS.map((a) => (
                      <Button
                        key={a.state}
                        size="sm"
                        variant="outline"
                        disabled={busy === 'participation' || tenant.overview?.participation_state === a.state}
                        onClick={() => transitionParticipation(tenant.overview?.business_id, a.state)}
                      >
                        {a.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {tenantTab === 'listings' && (
              (tenant.listings || []).length === 0
                ? <p className="text-sm text-gray-500">No listings.</p>
                : (
                  <div className="overflow-x-auto"><table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b"><tr>
                      {['Listing', 'Type', 'Category', 'Price', 'Visible', 'Media', 'Moderation', 'Compliance'].map((h) => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {tenant.listings.map((l) => (
                        <tr key={l.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-900">{l.name || '—'}</td>
                          <td className="px-3 py-2 text-gray-600">{l.item_type || <span className="text-gray-400 italic">not set</span>}</td>
                          <td className="px-3 py-2 text-gray-600">{l.category_name || <span className="text-gray-400 italic">unknown</span>}</td>
                          <td className="px-3 py-2 text-gray-600">{l.price ?? '—'}</td>
                          <td className="px-3 py-2 text-gray-600">{l.is_active === false ? 'Hidden' : 'Visible'}</td>
                          <td className="px-3 py-2 text-gray-600">{l.media_count ?? 0}</td>
                          <td className="px-3 py-2 text-gray-600">{l.moderation_status || <span className="text-gray-400 italic">not evaluated</span>}</td>
                          {/* "Compliant" when evaluated and clean; the issue list otherwise. Never a
                              bare "none", which reads as "not checked". */}
                          <td className="px-3 py-2 text-gray-600">
                            {(l.issues || []).length === 0
                              ? <span className="text-xs text-green-700">Compliant</span>
                              : <span className="text-xs text-amber-700">{l.issues.join(', ')}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                )
            )}

            {tenantTab === 'compliance' && (
              (tenant.compliance || []).length === 0
                ? <p className="text-sm text-green-700">Compliant — 0 findings.</p>
                : (
                  <ul className="space-y-2 text-sm">
                    {tenant.compliance.map((f, i) => (
                      <li key={i} className="p-3 rounded bg-amber-50 border border-amber-200">
                        <span className="font-medium text-gray-900">{f.listing_name || '—'}</span>
                        <span className="text-gray-600"> — {f.issue}</span>
                        {f.category_name && <span className="text-gray-500"> (category: {f.category_name})</span>}
                      </li>
                    ))}
                  </ul>
                )
            )}

            {tenantTab === 'audit' && (
              (tenant.audit || []).length === 0
                ? <p className="text-sm text-gray-500">No governance actions recorded for this tenant.</p>
                : (
                  <ul className="space-y-2 text-sm">
                    {tenant.audit.map((a, i) => (
                      <li key={i} className="p-3 rounded bg-gray-50">
                        <span className="font-medium text-gray-900">{a.action}</span>
                        <span className="text-gray-600"> by {a.actor || 'unknown'}</span>
                        <span className="text-gray-500"> · {a.timestamp}</span>
                        {a.reason && <p className="text-gray-600 mt-1">Reason: {a.reason}</p>}
                      </li>
                    ))}
                  </ul>
                )
            )}
          </CardContent>
        </Card>
      )}

      {section === 'categories' && (
        <Card className="bg-white shadow-md">
          <CardHeader className="pb-3"><CardTitle className="text-lg">Category Governance</CardTitle></CardHeader>
          <CardContent>
            {busy === 'categories' ? <p className="text-sm text-gray-400">Loading…</p>
              : categories.length === 0 ? <p className="text-sm text-gray-500">No categories.</p>
              : (
                <div className="overflow-x-auto"><table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b"><tr>
                    {['Category', 'Applies to', 'State', 'Listings using it', 'Replacement'].map((h) => (
                      <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {categories.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium text-gray-900">{c.name || '—'}</td>
                        <td className="px-4 py-2 text-gray-600">{c.applicability || '—'}</td>
                        <td className="px-4 py-2 text-gray-600">
                          {c.is_active === false
                            ? <span className="text-xs text-amber-700">Deprecated</span>
                            : <span className="text-xs text-green-700">Active</span>}
                        </td>
                        {/* Real usage counted from db.products — the number that makes deprecation
                            safe to reason about, rather than a stored count that can drift. */}
                        <td className="px-4 py-2 text-gray-600">{typeof c.listing_count === 'number' ? c.listing_count : '—'}</td>
                        <td className="px-4 py-2 text-gray-600">{c.replacement_id || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
          </CardContent>
        </Card>
      )}

      {section === 'moderation' && (
        <Card className="bg-white shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Moderation Review</CardTitle>
            <p className="text-xs text-gray-500">Source: db.products.moderation_status / is_approved</p>
          </CardHeader>
          <CardContent>
            {busy === 'moderation' ? <p className="text-sm text-gray-400">Loading…</p>
              : !moderation ? <p className="text-sm text-gray-500">No data.</p>
              : (
                <>
                  <p className="text-sm text-gray-600 mb-3">
                    {Object.entries(moderation.counts_by_status || {}).map((e) => e[0] + ': ' + e[1]).join(' · ') || 'No listings.'}
                  </p>
                  {/* Truthful empty states: nothing at all vs nothing REQUIRING review. */}
                  {(moderation.listings || []).length === 0
                    ? <p className="text-sm text-gray-500">No listings require moderation review.</p>
                    : (
                      <>
                        {!(moderation.counts_by_status || {}).flagged && (
                          <p className="text-sm text-green-700 mb-3">
                            No listings require moderation review. Every listing below has already been evaluated.
                          </p>
                        )}
                      <div className="overflow-x-auto"><table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b"><tr>
                          {['Business', 'Listing', 'Type', 'Moderation', 'Correction', 'Approved',
                            'Visible', 'Last review', 'Governed actions'].map((h) => (
                            <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">{h}</th>
                          ))}
                        </tr></thead>
                        <tbody className="divide-y divide-gray-100">
                          {moderation.listings.map((l) => {
                            const actions = l.allowed_decisions || [];
                            const rv = l.moderation_review;
                            return (
                            <tr key={l.id} className="hover:bg-gray-50 align-top">
                              <td className="px-4 py-2 text-gray-900">{l.business_name || '—'}</td>
                              <td className="px-4 py-2 text-gray-900">{l.name || '—'}</td>
                              <td className="px-4 py-2 text-gray-600">{l.item_type || <span className="text-gray-400 italic">unclassified</span>}</td>
                              <td className="px-4 py-2 text-gray-600">{l.moderation_status || <span className="text-gray-400 italic">not evaluated</span>}</td>
                              {/* Derived from the same moderation_review marker — not a second
                                  status field. Distinguishes "we asked" from "the tenant has
                                  resubmitted and it needs a decision". */}
                              <td className="px-4 py-2 text-gray-600">
                                {l.correction_state === 'awaiting_review'
                                  ? <span className="text-xs text-blue-700 bg-blue-50 px-2 py-0.5 rounded">Awaiting review</span>
                                  : l.correction_state === 'correction_requested'
                                    ? <span className="text-xs text-amber-800 bg-amber-50 px-2 py-0.5 rounded">Requested</span>
                                    : l.correction_outcome === 'accepted'
                                      ? <span className="text-xs text-green-800 bg-green-50 px-2 py-0.5 rounded">Accepted</span>
                                      : <span className="text-gray-400">—</span>}
                              </td>
                              <td className="px-4 py-2 text-gray-600">{l.is_approved === true ? 'Yes' : l.is_approved === false ? 'No' : '—'}</td>
                              <td className="px-4 py-2 text-gray-600">{l.is_active === false ? 'Hidden' : 'Visible'}</td>
                              {/* Only a REAL recorded review renders here. No invented finding text. */}
                              <td className="px-4 py-2 text-gray-600">
                                {rv ? (
                                  <span className="text-xs">
                                    {ACTION_LABELS[rv.decision] || rv.decision}
                                    {rv.reviewed_by ? ` · ${rv.reviewed_by}` : ''}
                                    {rv.reason ? ` — ${rv.reason}` : ''}
                                    {rv.tenant_submitted_at
                                      ? ` · tenant resubmitted ${String(rv.tenant_submitted_at).slice(0, 10)}`
                                      : ''}
                                  </span>
                                ) : <span className="text-gray-400 italic">none</span>}
                              </td>
                              <td className="px-4 py-2">
                                <div className="flex gap-1 flex-wrap">
                                  {actions.map((a) => (
                                    <Button key={a} size="sm" variant="outline" disabled={!!busy}
                                      className={a === 'confirm' ? 'text-red-700 border-red-300' : ''}
                                      onClick={() => openReview(l, a)}>
                                      {ACTION_LABELS[a]}
                                    </Button>
                                  ))}
                                  {/* Distinct from a moderation verdict: moves is_active only. */}
                                  <Button size="sm" variant="outline" disabled={!!busy}
                                    onClick={() => setVisibility(l, l.is_active === false)}>
                                    {l.is_active === false ? 'Restore visibility' : 'Suspend visibility'}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );})}
                        </tbody>
                      </table></div>
                      </>
                    )}
                </>
              )}
          </CardContent>
        </Card>
      )}

      {/* ONE governed review modal for every action that needs a reason. Shows the business, the
          listing, the CURRENT state, the chosen action and its consequence, so the operator is
          never guessing what a click will do. No raw identifier is displayed. */}
      {review && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-5 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {ACTION_LABELS[review.action]}
            </h3>
            <dl className="text-sm space-y-1">
              <div className="flex gap-2"><dt className="text-gray-500 w-32">Business</dt>
                <dd className="text-gray-900">{review.listing.business_name || 'Unknown business'}</dd></div>
              <div className="flex gap-2"><dt className="text-gray-500 w-32">Listing</dt>
                <dd className="text-gray-900">{review.listing.name || '—'}</dd></div>
              <div className="flex gap-2"><dt className="text-gray-500 w-32">Current state</dt>
                <dd className="text-gray-900">{review.listing.moderation_status || 'not evaluated'}</dd></div>
            </dl>
            <p className={'text-sm p-3 rounded ' + (review.action === 'confirm'
              ? 'bg-red-50 text-red-800 border border-red-200'
              : 'bg-gray-50 text-gray-700')}>
              {ACTION_CONSEQUENCE[review.action]}
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason{ACTION_REQUIRES_REASON[review.action] ? ' (required)' : ' (optional)'}
              </label>
              <textarea
                className="w-full border border-gray-300 rounded p-2 text-sm"
                rows={3}
                value={reviewReason}
                onChange={(e) => setReviewReason(e.target.value)}
                placeholder="Recorded in the governance audit trail."
              />
            </div>
            {errors.review && (
              <p className="text-sm text-red-700">{errors.review.message}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" disabled={reviewSubmitting}
                onClick={() => { setReview(null); setReviewReason(''); clearScope('review'); }}>
                Cancel
              </Button>
              <Button size="sm" onClick={submitReview}
                disabled={reviewSubmitting
                  || (ACTION_REQUIRES_REASON[review.action] && !reviewReason.trim())}>
                {reviewSubmitting ? 'Recording…' : ACTION_LABELS[review.action]}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Analysis result (real, computed from products) */}
      {analysis && (
        <Card className="bg-white shadow-md">
          <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-lg"><Sparkles className="h-5 w-5 text-purple-600" />Marketplace Analysis</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-3">{analysis.total_products} products · {analysis.active_products} active · {analysis.inactive_products} inactive</p>
            {(analysis.categories || []).length === 0 ? <p className="text-sm text-gray-500">No product categories.</p> : (
              <div className="space-y-1">
                {analysis.categories.map((c, i) => (
                  <div key={i} className="flex items-center justify-between py-1 border-b last:border-0">
                    <span className="text-sm text-gray-800">{c.category}</span>
                    <span className="text-sm text-gray-600">{c.count} ({c.pct}%)</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Flagged content (real) */}
      {flagged && (
        <Card className="bg-white shadow-md">
          <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-lg"><Flag className="h-5 w-5 text-red-600" />Flagged Content</CardTitle></CardHeader>
          <CardContent>
            {flagged.length === 0 ? <p className="text-sm text-gray-500">No flagged content.</p> : (
              <div className="space-y-1">
                {flagged.map((f, i) => (
                  <div key={i} className="flex items-center justify-between py-1 border-b last:border-0">
                    <span className="text-sm text-gray-800 truncate">{f.title || f.reason || f.content_id || f.id || 'Flagged item'}</span>
                    <span className="text-xs text-gray-500">{f.status || f.type || ''}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="bg-white shadow-md">
        <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-lg"><Store className="h-5 w-5 text-purple-600" />Showroom Registry (live)</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-gray-400">Loading…</p>
            : showrooms.length === 0 ? (
              <div className="text-center py-8 text-gray-500"><Package className="h-10 w-10 mx-auto mb-2 text-gray-300" /><p>No showrooms registered yet.</p></div>
            ) : (
              <div className="overflow-x-auto"><table className="w-full text-sm">
                <thead className="bg-gray-50 border-b"><tr>
                  {/* THREE DIFFERENT FACTS, three columns. "active test" collapsed platform
                      participation, the tenant's own publication flag and the environment marker
                      into one string, which reads as a single state and hides which one is actually
                      withholding a showroom from discovery. */}
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Business</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Marketplace State</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Showroom Visibility</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Environment</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Test Data</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Discoverable</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Listings</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Findings</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Contact</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Showroom Created</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {showrooms.map((s, i) => (
                    <tr key={s.business_id || i} className="hover:bg-gray-50">
                      {/* D3: drop the raw business_id fallback — show a friendly dash instead of an internal id. */}
                      <td className="px-4 py-2 font-medium text-gray-900">
                        <button
                          type="button"
                          className="text-purple-700 hover:underline text-left"
                          onClick={() => openTenant(s.business_id)}
                        >
                          {s.name || s.business_name || '—'}
                        </button>
                      </td>
                      <td className="px-4 py-2 text-gray-600">
                        {s.participation_state
                          ? PARTICIPATION_LABELS[s.participation_state] || s.participation_state
                          : <span className="text-gray-400 italic">not set</span>}
                      </td>
                      <td className="px-4 py-2 text-gray-600">{s.listing_visibility || '—'}</td>
                      {/* ENVIRONMENT — which deployment this row lives in. Reported by the server;
                          never derived from is_test, which says nothing about the environment. */}
                      <td className="px-4 py-2 text-gray-600">
                        {s.environment
                          ? <span className="text-xs text-gray-700">{titleCase(s.environment)}</span>
                          : <span className="text-gray-400 italic">unavailable</span>}
                      </td>
                      {/* TEST DATA — whether this row is a fixture. A separate fact, and NOT an
                          input to discoverability (owner ruling 2026-08-28). */}
                      <td className="px-4 py-2 text-gray-600">
                        {s.is_test
                          ? <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded">Yes</span>
                          : <span className="text-xs text-gray-600">No</span>}
                      </td>
                      <td className="px-4 py-2 text-gray-600">
                        {s.publicly_discoverable
                          ? <span className="text-xs text-green-700">Yes</span>
                          : <span className="text-xs text-gray-500">Withheld</span>}
                      </td>
                      <td className="px-4 py-2 text-gray-600">{
                        typeof s.listings === 'number'
                          ? `${s.active_listings ?? 0} active / ${s.listings}`
                          : <span className="text-gray-400 italic">unavailable</span>
                      }</td>
                      <td className="px-4 py-2 text-gray-600">
                        {/* A measured zero says "Compliant"; a value that was never collected says
                            "unavailable". The two must never render the same way. */}
                        {typeof s.findings === 'number'
                          ? (s.findings > 0
                              ? <span className="text-xs text-amber-700">{s.findings}</span>
                              : <span className="text-xs text-green-700">Compliant</span>)
                          : <span className="text-gray-400 italic">unavailable</span>}
                      </td>
                      <td className="px-4 py-2 text-gray-600">{s.contact || <span className="text-gray-400 italic">not recorded</span>}</td>
                      <td className="px-4 py-2 text-gray-600">{s.joinedAt || <span className="text-gray-400 italic">not recorded</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MarketplaceControl;
